import { useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    ColumnDef,
    SortingState,
    RowSelectionState,
    OnChangeFn,
} from "@tanstack/react-table";
import css from "./TanstackTable.module.scss";

export interface ColumnMeta {
    width?: string | number;
    minWidth?: string | number;
    maxWidth?: string | number;
    cellAlign?: "left" | "center" | "right";
    headerAlign?: "left" | "center" | "right";
}

export interface TanstackTableProps<T> {
    data: T[];
    columns: ColumnDef<T, any>[];
    emptyMessage?: string;
    className?: string;
    enableSorting?: boolean;
    enableRowSelection?: boolean;
    onRowClick?: (row: T) => void;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

function getColumnWidthStyle(meta?: ColumnMeta): React.CSSProperties {
    if (!meta) return {};

    const style: React.CSSProperties = {};

    if (meta.width !== undefined) {
        style.width = typeof meta.width === 'number' ? `${meta.width}px` : meta.width;
    }
    if (meta.minWidth !== undefined) {
        style.minWidth = typeof meta.minWidth === 'number' ? `${meta.minWidth}px` : meta.minWidth;
    }
    if (meta.maxWidth !== undefined) {
        style.maxWidth = typeof meta.maxWidth === 'number' ? `${meta.maxWidth}px` : meta.maxWidth;
    }

    return style;
}

function getHeaderAlignStyle(meta?: ColumnMeta): React.CSSProperties {
    const align = meta?.headerAlign;
    if (!align) return {};

    switch (align) {
        case "center":
            return { justifyContent: "center" };
        case "right":
            return { justifyContent: "flex-end" };
        case "left":
        default:
            return { justifyContent: "flex-start" };
    }
}

function getCellAlignStyle(meta?: ColumnMeta): React.CSSProperties {
    const align = meta?.cellAlign;
    if (!align) return {};

    return { textAlign: align };
}

export default function TanstackTable<T>({
    data,
    columns,
    emptyMessage = "Нет данных",
    className = "",
    enableSorting = true,
    enableRowSelection = false,
    onRowClick,
    rowSelection,
    onRowSelectionChange,
}: TanstackTableProps<T>) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            rowSelection: rowSelection ?? internalRowSelection,
        },
        onSortingChange: setSorting,
        onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
        enableRowSelection,
    });

    return (
        <div className={`${css.tanstackTableWrapper} ${className}`}>
            <table className={css.tanstackTable}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const meta = header.column.columnDef.meta as ColumnMeta | undefined;
                                return (
                                    <th
                                        key={header.id}
                                        onClick={
                                            enableSorting && header.column.getCanSort()
                                                ? header.column.getToggleSortingHandler()
                                                : undefined
                                        }
                                        className={
                                            enableSorting && header.column.getCanSort()
                                                ? css.sortable
                                                : ""
                                        }
                                        style={getColumnWidthStyle(meta)}
                                    >
                                        <div
                                            className={css.headerContent}
                                            style={getHeaderAlignStyle(meta)}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef.header,
                                                      header.getContext()
                                                  )}
                                            {enableSorting &&
                                                header.column.getIsSorted() && (
                                                    <span className={css.sortIndicator}>
                                                        {header.column.getIsSorted() === "asc"
                                                            ? " ▲"
                                                            : " ▼"}
                                                    </span>
                                                )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className={css.emptyMessage}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => onRowClick?.(row.original)}
                                className={onRowClick ? css.rowClickable : ""}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                                    return (
                                        <td
                                            key={cell.id}
                                            style={{
                                                ...getColumnWidthStyle(meta),
                                                ...getCellAlignStyle(meta),
                                            }}
                                        >
                                            <div className={css.cellContent}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
